import { useMemo, useState } from "react";
import { FiPlus, FiUsers } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { Pagination } from "newui/components/Pagination";
import { Help } from "newui/components/ui/Help";
import { useCurrentUserId } from "hooks/auth";
import {
  TOUR_USERS,
  TOUR_USER_INVITED,
  TOUR_USER_ASSIGNMENT,
  buildUserManagementTopics,
  isTourDummyUser,
} from "guides/features/Auth";
import { useHasRbacV2 } from "hooks/featureFlags";
import { useUserAssignments } from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { AssignWizard } from "newui/features/Settings/components/RolesPermissions/AssignWizard/AssignWizard";
import { AssignRoleDrawer } from "newui/features/Settings/components/RolesPermissions/AssignRoleDrawer";
import { UserAccessMatrixModal } from "newui/features/Settings/components/RolesPermissions/UserAccessMatrixModal";
import { ReadOnlyGuard } from "newui/features/ExecutiveView";
import { useModuleCan } from "newui/features/RolesPermissions/hooks/useModuleCan";
import { RBAC_MODULE_KEYS } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { NoAccessMessage, ReadOnlyNotice } from "newui/components/AccessGate";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import type { UserListItem, UserListFilters } from "../../types/userInvite";
import { UserListTable } from "./UserListTable/UserListTable";
import { UserListFiltersComponent } from "./UserListTable/UserListFilters";
import { UserDrawer } from "./UserDrawer";
import { UserStatusConfirmModal } from "./UserStatusConfirmModal";
import { useUsersQuery } from "./hooks/useUsersQuery";
import { useDeactivateUserMutation } from "./hooks/useDeactivateUserMutation";

export const UserListPage = () => {
  const [filters, setFilters] = useState<UserListFilters>({
    page: 1,
    status: ["active", "invited"],
  });
  const [isInviteDrawerOpen, setIsInviteDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<UserListItem | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<UserListItem | null>(null);
  // When the guided tour is active we inject demo rows (see TOUR_USERS) so
  // the steps have stable targets for resend / deactivate / reactivate.
  const [isTourActive, setIsTourActive] = useState(false);

  // Threaded into the table so a row can refuse to open the edit drawer for the
  // viewer's own record — an administrator may not administer themselves here
  // (see UserRowActions). The row reads it as a prop rather than calling the
  // hook itself; UserListRow explains why.
  const currentUserId = useCurrentUserId();

  // PRO-413: RBAC v2 is ADDITIVE. When on, we layer role management (role pills,
  // per-user "Manage roles" drawer, bulk "Assign template" wizard) onto this page
  // without removing any of the existing user-management functionality.
  const hasRbacV2 = useHasRbacV2();
  const [isAssignWizardOpen, setIsAssignWizardOpen] = useState(false);
  const [manageRolesTarget, setManageRolesTarget] = useState<UserAssignment | null>(null);
  // PRO-413 #UM1: the user whose read-only effective-access matrix is open.
  const [accessMatrixTarget, setAccessMatrixTarget] = useState<UserListItem | null>(null);
  // Users pre-selected when the wizard opens. Empty = the bulk "Allocate roles"
  // entry point (pick any users); seeded with one id when jumping from a single
  // user's "Manage roles" drawer to scope their role to projects/RFx events.
  const [wizardInitialUserIds, setWizardInitialUserIds] = useState<number[]>([]);
  // PRO-732a: true only for the single-user "Manage roles" jump-in — the bulk
  // "Allocate Roles" entry point always opens with this false, even though it
  // seeds no users (an empty array), so Step 1 keeps its search/pick-list.
  const [isWizardSingleUserMode, setIsWizardSingleUserMode] = useState(false);
  // PRO-730 — lets the "Allocate roles to several users" guided tour drive the
  // wizard from Step 1 to Step 2 (so `wizard-confirm`, which only renders on
  // Step 2, has something to target) without selecting a real user/role.
  const [assignWizardTourStep, setAssignWizardTourStep] = useState<1 | 2 | undefined>(undefined);

  /*
   * PRO-709: user management is the residual `settings` module — company
   * administration (users, company 2FA governance, notification config).
   *
   * The defect this replaces: the page rendered the full table plus "Invite
   * User" and "Allocate Roles" to anyone, then fired the RBAC calls behind
   * them — /rbac/users, /rbac/roles, /rbac/role-templates, /rbac/scopes and the
   * per-user matrix — which 403'd one by one and stacked FIVE error toasts on a
   * page the user should never have been shown.
   *
   * `/api/v1/companies/{id}/users` currently returns 200 for non-admins (raised
   * separately with the product owner), so the denial CANNOT be inferred from a
   * failing request — it is gated on the capability, and the users query itself
   * is disabled so no request goes out at all.
   */
  const {
    canView: canViewUsers,
    canEdit: canManageUsers,
    isLoading: isPermissionsLoading,
  } = useModuleCan(RBAC_MODULE_KEYS.settings);
  const hasUsersAccess = canViewUsers || canManageUsers;

  const { data, isLoading } = useUsersQuery(filters, { enabled: hasUsersAccess });
  const deactivate = useDeactivateUserMutation();

  /*
   * Every RBAC v2 surface on this page — the role pills (which fetch the roles
   * list per row), the assign wizard and the role drawer — is administrative and
   * is refused for a view-only user. It is therefore gated on `canManageUsers`,
   * not on view: rendering it for a viewer is exactly the 403-toast pile this
   * ticket removes. A viewer still sees each user's legacy role name from the
   * users payload, so the column is not empty.
   */
  const rbacEnabled = hasRbacV2 && canManageUsers;

  // RBAC assignment records, keyed by user id, so each legacy row can render its
  // roles. Ignored entirely when the flag is off.
  const { users: assignmentUsers } = useUserAssignments({ enabled: rbacEnabled });
  const assignmentsByUserId = useMemo(() => {
    const map = new Map<number, UserAssignment>();
    if (rbacEnabled) {
      assignmentUsers.forEach((assignment) => map.set(assignment.id, assignment));
    }
    return map;
  }, [rbacEnabled, assignmentUsers]);

  // Re-derive the manage-roles target from the LIVE assignments map so the drawer's
  // "Current roles" reflects server state after an assign/remove mutation invalidates
  // the query — `manageRolesTarget` itself is only the snapshot captured at open time.
  const manageRolesUser = useMemo(() => {
    if (manageRolesTarget === null) {
      return null;
    }
    return assignmentsByUserId.get(manageRolesTarget.id) ?? manageRolesTarget;
  }, [manageRolesTarget, assignmentsByUserId]);

  const realUsers = data?.data ?? [];
  const users = isTourActive ? [...TOUR_USERS, ...realUsers] : realUsers;

  const handleConfirmDeactivate = () => {
    if (!deactivateTarget) {
      return;
    }
    // Dummy tour rows must never hit the API.
    if (isTourDummyUser(deactivateTarget)) {
      setDeactivateTarget(null);
      return;
    }
    deactivate.mutate(deactivateTarget.id, {
      onSettled: () => setDeactivateTarget(null),
    });
  };

  const handleEdit = (user: UserListItem) => {
    // Dummy tour rows don't have a backing record — don't open the edit drawer.
    if (isTourDummyUser(user)) {
      return;
    }
    setEditTarget(user);
  };

  const handleDeactivate = (user: UserListItem) => {
    if (isTourDummyUser(user)) {
      return;
    }
    setDeactivateTarget(user);
  };

  const openBulkAssign = () => {
    setWizardInitialUserIds([]);
    setIsWizardSingleUserMode(false);
    setIsAssignWizardOpen(true);
  };

  const closeAssignWizard = () => {
    setIsAssignWizardOpen(false);
    setWizardInitialUserIds([]);
    setIsWizardSingleUserMode(false);
  };

  // Jump from a single user's "Manage roles" drawer straight into the wizard with
  // that user pre-selected, so their role can be scoped to projects/RFx events.
  const handleScopeToProjects = (assignment: UserAssignment) => {
    setManageRolesTarget(null);
    setWizardInitialUserIds([assignment.id]);
    setIsWizardSingleUserMode(true);
    setIsAssignWizardOpen(true);
  };

  const handleManageRoles = (user: UserListItem) => {
    if (isTourDummyUser(user)) {
      return;
    }
    // Prefer the server-authoritative assignment record; fall back to a minimal
    // shell so a not-yet-assigned user can still be given their first role.
    const existing = assignmentsByUserId.get(user.id);
    setManageRolesTarget(
      existing ?? {
        id: user.id,
        name: `${user.first_name} ${user.last_name}`.trim(),
        email: user.email,
        migration_status: "pending",
        assignments: [],
        flags: { template_creation: false },
      },
    );
  };

  const handleViewAccess = (user: UserListItem) => {
    if (isTourDummyUser(user)) {
      return;
    }
    setAccessMatrixTarget(user);
  };

  /*
   * PRO-730 — the "Manage a user's roles" / "View a user's access" guided
   * tours point at the dummy tour row's actions, which `handleManageRoles`/
   * `handleViewAccess` deliberately refuse (isTourDummyUser guard above, so
   * real clicks on a dummy row never open anything). These tour-only openers
   * bypass that guard on purpose — they set the SAME state those handlers
   * would, seeded with the dummy `TOUR_USER_ASSIGNMENT` / `TOUR_USER_INVITED`
   * shells, so the drawer/modal opens for the tour without ever touching a
   * real user. `AssignRoleDrawer` and `UserAccessMatrixModal` independently
   * refuse to fire mutations/queries for a negative (dummy) id.
   */
  const openManageRolesForTour = () => {
    setManageRolesTarget(TOUR_USER_ASSIGNMENT);
  };

  const closeManageRolesForTour = () => {
    setManageRolesTarget(null);
  };

  const openViewAccessForTour = () => {
    setAccessMatrixTarget(TOUR_USER_INVITED);
  };

  const closeViewAccessForTour = () => {
    setAccessMatrixTarget(null);
  };

  // The "Allocate roles to several users" tour reuses the real entry point
  // (no dummy user involved — the wizard's own picker is demonstrated empty).
  const openAllocateRolesForTour = () => {
    setAssignWizardTourStep(undefined);
    openBulkAssign();
  };

  const advanceAssignWizardForTour = () => {
    setAssignWizardTourStep(2);
  };

  const closeAllocateRolesForTour = () => {
    closeAssignWizard();
    setAssignWizardTourStep(undefined);
  };

  // Not memoized: every function passed in just wraps a `useState` setter
  // (stable), so rebuilding the topic list each render is cheap and keeps
  // this in sync with `rbacEnabled` without an exhaustive-deps dependency
  // list full of locally-declared closures.
  const helpTopics = buildUserManagementTopics({
    openInviteDrawer: () => setIsInviteDrawerOpen(true),
    closeInviteDrawer: () => setIsInviteDrawerOpen(false),
    openManageRoles: openManageRolesForTour,
    closeManageRoles: closeManageRolesForTour,
    openViewAccess: openViewAccessForTour,
    closeViewAccess: closeViewAccessForTour,
    openAllocateRoles: openAllocateRolesForTour,
    advanceWizardToScopeStep: advanceAssignWizardForTour,
    closeAllocateRoles: closeAllocateRolesForTour,
    rbacEnabled,
  });

  if (isPermissionsLoading) {
    return <LoadingSpinner label="Loading users..." verticalPadding={8} />;
  }

  if (!hasUsersAccess) {
    return (
      <NoAccessMessage
        data-testid="user-list-no-access"
        description="You do not have permission to manage company users. Ask a company administrator if you need access."
      />
    );
  }

  return (
    <div className="rounded-lg bg-white shadow-sm border border-gray-200">
      <div className="flex items-center justify-between gap-4 px-4 py-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div>
            <Help
              parent={<h2 className="text-lg font-semibold text-gray-900">User Management</h2>}
              topics={helpTopics}
              label="Show me how to invite and manage users"
              data-testid="user-list-help"
              onBeforeShowMe={() => {
                // isTourActive must be true for every topic — each one relies on the
                // dummy TOUR_USERS rows for stable, side-effect-free targets.
                setIsTourActive(true);
              }}
              onAfterShowMe={() => {
                // Tear down every surface ANY topic may have opened, regardless of
                // which one actually ran.
                setIsTourActive(false);
                setIsInviteDrawerOpen(false);
                setManageRolesTarget(null);
                setAccessMatrixTarget(null);
                closeAssignWizard();
                setAssignWizardTourStep(undefined);
              }}
            />
            <p className="text-sm text-gray-500">Manage your company users and their access.</p>
          </div>
        </div>
        <ReadOnlyGuard>
          <div className="flex items-center gap-2">
            {rbacEnabled && (
              <Button
                btntype="plain-border"
                onClick={openBulkAssign}
                className="flex items-center gap-2"
                data-testid="open-assign-wizard"
              >
                <FiUsers className="h-4 w-4" />
                Allocate Roles
              </Button>
            )}
            {canManageUsers && (
              <Button
                onClick={() => setIsInviteDrawerOpen(true)}
                className="flex items-center gap-2"
                data-testid="invite-user-button"
              >
                <FiPlus className="h-4 w-4" />
                Invite User
              </Button>
            )}
          </div>
        </ReadOnlyGuard>
      </div>

      {!canManageUsers && (
        <div className="px-4 pt-4">
          <ReadOnlyNotice
            data-testid="user-list-read-only"
            message="You have read-only access to user management. Inviting, editing and role changes are disabled."
          />
        </div>
      )}

      <UserListFiltersComponent filters={filters} onChange={(f) => setFilters({ ...f, page: 1 })} />

      <div data-testid="user-list-table">
        <UserListTable
          users={users}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDeactivate={handleDeactivate}
          canManageUsers={canManageUsers}
          currentUserId={currentUserId}
          rbacEnabled={rbacEnabled}
          assignmentsByUserId={assignmentsByUserId}
          onManageRoles={handleManageRoles}
          onViewAccess={handleViewAccess}
        />
      </div>

      <Pagination
        currentPage={filters.page ?? 1}
        rowsPerPage={data?.meta?.per_page ?? 25}
        totalItems={data?.meta?.total ?? 0}
        onPageChange={(newPage) => setFilters((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={() => undefined}
        disabled={isLoading}
      />

      {/* Unified drawer: invite (ghost two-step) and edit share one component.
          onManageRoles/onViewAccess intentionally do NOT close this drawer —
          the role/scope drawer stacks on top and returns the admin to their
          in-progress form (invite draft or edit) with state intact. */}
      <UserDrawer
        mode="invite"
        isOpen={isInviteDrawerOpen}
        onClose={() => setIsInviteDrawerOpen(false)}
        rbacEnabled={rbacEnabled}
        onManageRoles={handleManageRoles}
        onViewAccess={handleViewAccess}
      />

      <UserDrawer
        mode="edit"
        user={editTarget}
        isOpen={editTarget !== null}
        onClose={() => setEditTarget(null)}
        rbacEnabled={rbacEnabled}
        onManageRoles={handleManageRoles}
        onViewAccess={handleViewAccess}
      />

      {rbacEnabled && (
        <>
          <AssignWizard
            isOpen={isAssignWizardOpen}
            onClose={closeAssignWizard}
            users={assignmentUsers}
            initialUserIds={wizardInitialUserIds}
            singleUserMode={isWizardSingleUserMode}
            forcedStep={assignWizardTourStep}
          />
          <AssignRoleDrawer
            isOpen={manageRolesUser !== null}
            onClose={() => setManageRolesTarget(null)}
            user={manageRolesUser}
            onScopeToProjects={
              manageRolesUser
                ? () => handleScopeToProjects(manageRolesUser)
                : undefined
            }
          />
          <UserAccessMatrixModal
            isOpen={accessMatrixTarget !== null}
            userId={accessMatrixTarget?.id}
            userName={
              accessMatrixTarget
                ? `${accessMatrixTarget.first_name} ${accessMatrixTarget.last_name}`.trim()
                : undefined
            }
            onClose={() => setAccessMatrixTarget(null)}
          />
        </>
      )}

      {deactivateTarget && (
        <UserStatusConfirmModal
          isOpen
          action="deactivate"
          firstName={deactivateTarget.first_name}
          isLoading={deactivate.isPending}
          onConfirm={handleConfirmDeactivate}
          onClose={() => setDeactivateTarget(null)}
        />
      )}
    </div>
  );
};
